
import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiBootstrapModule from "angular-ui-bootstrap";
import kendoDirectives from "../../shims/kendoDirectives";
import uiRouterModule from "@uirouter/angularjs";

import UserInfoServiceModule from "../../services/UserInfoService";
import StatusClassServiceModule from "../../services/StatusClassService";
import TrackingCalendarDataServiceModule from "../../services/TrackingCalendarDataService";
import TrackingEventReportDataServiceModule from "../../services/TrackingEventReportDataService";

import StatusLabelComponentModule from "../../directives/StatusLabel";
import CalendarEventDateTimeComponentModule from "../../directives/CalendarEventDateTime/CalendarEventDateTime";
import OfficeTypeLabelTypeFilterModule from "../../filters/OfficeTypeLabelTypeFilter";
import CurrentCandidaciesFilterModule from "../tracking-event-editor/CurrentCandidaciesFilter";

import EventTrackingReportDialogController from "../event-tracking-report-dialog/EventTrackingReportDialog";
import CalendarEventDirectLinkDialogController from "./CalendarEventDirectLinkDialogController";

import EventViewStateConstants from "./TrackingCalendarEventViewConstants";

import Assert from "tbAssert";
import DependencyListBuilder from "../../utility/DependencyListBuilder";
import MessageQueue from "../../models/MessageQueue";

const errorMessageType = "danger";
const mCurrentErrorMessages = new WeakMap();

const mLogSvc = new WeakMap();
const mQSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mUiRouterStateSvc = new WeakMap();
const mModalSvc = new WeakMap();
const mTrackingCalendarDataSvc = new WeakMap();
const mStatusClassSvc = new WeakMap();
const mUserInfoSvc = new WeakMap();
const mTrackingEventReportDataSvc = new WeakMap();

const mCurrentCalendarEvent = new WeakMap();
const mCalendarEventId = new WeakMap();

const mDisplayEventInfoPanel = new WeakMap();
const mIsInitialized = new WeakMap();

const mEventActionMenuIsOpen = new WeakMap();

const mCanViewTrackingReport = new WeakMap();

const mDescriptionVisible = new WeakMap();
const mPressDetailsVisible = new WeakMap();
const mNotesVisible = new WeakMap();

const defaultPanelStatusClass = "panel-default";
const mPanelStatusClass = new WeakMap();

const mAdditionalPanelClasses = new WeakMap();

const mNavigateToEventEditorFn = new WeakMap();

const modalBackdropClass = "tbIn";
const modalWindowClass = "tbIn";
const modalSizeLarge = "lg";

class CalendarEventInfoPanelController {
    constructor($scope,
                $log,
                $q,
                $timeout,
                $uibModal,
                $state,
                TrackingCalendarData,
                StatusClass,
                UserInfoService,
                TrackingEventReportData)
    {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");

        Assert.StrictAssert($uibModal != null, "Assertion Failure: Missing UI Modal Service Dependency");
        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");

        Assert.StrictAssert(TrackingCalendarData != null, "Assertion Failure: Missing Tracking Calendar Data Service Dependency");
        Assert.StrictAssert(StatusClass != null, "Assertion Failure: Missing Status Class Service Dependency");
        Assert.StrictAssert(UserInfoService != null, "Assertion Failure: Missing User Info Service Dependency");
        Assert.StrictAssert(TrackingEventReportData != null, "Assertion Failure: Missing Tracking Event Report Data Service Dependency");

        mCurrentErrorMessages.set(this, new MessageQueue(errorMessageType, null));

        mLogSvc.set(this, $log);
        mQSvc.set(this, $q);
        mTimeoutSvc.set(this, $timeout);
        mUiRouterStateSvc.set(this, $state);
        mModalSvc.set(this, $uibModal);
        mTrackingCalendarDataSvc.set(this, TrackingCalendarData);
        mStatusClassSvc.set(this, StatusClass);
        mUserInfoSvc.set(this, UserInfoService);
        mTrackingEventReportDataSvc.set(this, TrackingEventReportData);

        mCurrentCalendarEvent.set(this, null);
        mDisplayEventInfoPanel.set(this, false);
        mIsInitialized.set(this, false);

        mEventActionMenuIsOpen.set(this, false);

        mCanViewTrackingReport.set(this, false);
        mDescriptionVisible.set(this, false);
        mPressDetailsVisible.set(this, false);
        mNotesVisible.set(this, false);

        mPanelStatusClass.set(this, defaultPanelStatusClass);

        $scope.$watch(
            () => this.calendarEventId,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    if (Number.isInteger(newVal)) {
                        this.loadCalendarEvent().then(
                            (didSucceed) => {
                                if (didSucceed === true) {
                                    this.showEventInfoPanel();
                                }
                            },
                            (didSucceed) => {
                                if (didSucceed !== true) {
                                    this.showEventInfoPanel();
                                }
                            });
                    }
                    else {
                        mCurrentCalendarEvent.set(this, null);
                    }
                }
            }
        );
    }

    get currentErrorMessages() {
        return mCurrentErrorMessages.get(this).messages;
    }

    get userInfo() {
        return mUserInfoSvc.get(this);
    }

    get currentCalendarEvent() {
        return mCurrentCalendarEvent.get(this);
    }

    get calendarEventId() {
        return mCalendarEventId.get(this);
    }

    set calendarEventId(updatedVal) {
        mCalendarEventId.set(this, updatedVal);
    }

    get displayEventInfoPanel() {
        return mDisplayEventInfoPanel.get(this);
    }

    set displayEventInfoPanel(updatedVal) {
        mDisplayEventInfoPanel.set(this, updatedVal);
    }

    get isInitialized() {
        return mIsInitialized.get(this);
    }

    get eventActionMenuIsOpen() {
        return mEventActionMenuIsOpen.get(this);
    }

    //Setter called in ui.bootstrap dropdown directive (via template)
    set eventActionMenuIsOpen(updatedVal) {
        mEventActionMenuIsOpen.set(this, updatedVal);
    }

    get canViewTrackingReport() {
        return mCanViewTrackingReport.get(this);
    }

    get descriptionVisible() {
        return mDescriptionVisible.get(this);
    }

    set descriptionVisible(updatedVal) {
        mDescriptionVisible.set(this, updatedVal);
    }

    get pressDetailsVisible() {
        return mPressDetailsVisible.get(this);
    }

    set pressDetailsVisible(updatedVal) {
        mPressDetailsVisible.set(this, updatedVal);
    }

    get notesVisible() {
        return mNotesVisible.get(this);
    }

    set notesVisible(updatedVal) {
        mNotesVisible.set(this, updatedVal);
    }

    get panelStatusClass() {
        return mPanelStatusClass.get(this);
    }

    get additionalPanelClasses() {
        return mAdditionalPanelClasses.get(this);
    }

    set additionalPanelClasses(updatedVal) {
        mAdditionalPanelClasses.set(this, updatedVal);
    }

    get navigateToEventEditorFn() {
        return mNavigateToEventEditorFn.get(this);
    }

    set navigateToEventEditorFn(updatedFn) {
        mNavigateToEventEditorFn.set(this, updatedFn);
    }

    //Methods

    hasErrorState() {
        return ((mCurrentErrorMessages.get(this).messageCount > 0) || (this.currentCalendarEvent == null));
    }

    closeEventInfoPanel() {
        mDisplayEventInfoPanel.set(this, false);
    }

    showEventInfoPanel() {
        if (!this.isInitialized) {
            mIsInitialized.set(this, true);
            mTimeoutSvc.get(this)(() => {
                mDisplayEventInfoPanel.set(this, true);
            }, 50);
        }
        else {
            mDisplayEventInfoPanel.set(this, true);
        }
    }

    loadCalendarEvent() {

        mCurrentErrorMessages.get(this).clearMessages();

        return mTrackingCalendarDataSvc.get(this)
            .getEvent(this.calendarEventId)
            .then(
                (currentEvent) => {
                    mCurrentCalendarEvent.set(this, currentEvent);
                    let eventStatusClass = mStatusClassSvc.get(this).getBootstrapStatusLabelType(
                        this.currentCalendarEvent.statusName
                    );

                    if (eventStatusClass != null) {
                        mPanelStatusClass.set(this, `panel-${eventStatusClass}`);
                    }
                    else {
                        mPanelStatusClass.set(this, defaultPanelStatusClass);
                    }

                    mCanViewTrackingReport.set(this, this.currentCalendarEvent.trackingStatus.isReported === true);

                    return true;
                },
                (loadingErrorMessage) => {
                    mLogSvc.get(this).error(loadingErrorMessage);
                    mCurrentCalendarEvent.set(this, null);
                    mPanelStatusClass.set(this, defaultPanelStatusClass);
                    mCanViewTrackingReport.set(this, false);
                    mCurrentErrorMessages.get(this).publishMessage(loadingErrorMessage);

                    return mQSvc.get(this).reject(false);
                }
            );
    }

    $onInit() {
        if (Number.isInteger(this.calendarEventId)) {
            this.loadCalendarEvent().then(
                (didSucceed) => {
                    if (didSucceed === true) {
                        this.showEventInfoPanel();
                    }
                },
                (didSucceed) => {
                    if (didSucceed !== true) {
                        this.showEventInfoPanel();
                    }
                }
            );
        }
        else {
            mCurrentCalendarEvent.set(this, null);
        }
    }

    loadEventEditor() {
        if ((mCurrentCalendarEvent.get(this) != null) &&
            (this.userInfo != null) &&
            (this.userInfo.hasTrackerRights === true)) {

            let currentEventId = this.currentCalendarEvent.EventId;
            this.navigateToEventEditorFn({eventId: currentEventId});
        }
        else {
            mLogSvc.get(this).warn(
                "Ignored request to load the event editor when there was no current calendar event, no current user information service loaded, or the user does not have tracker rights"
            );
        }
    }

    showTrackingReport() {

        if (this.currentCalendarEvent == null) {
            mLogSvc.get(this).warn(
                "Ignored request to show a tracking report when there is no calendar event loaded."
            );
            return;
        }

        if (this.canViewTrackingReport !== true) {
            mLogSvc.get(this).warn(
                "Ignored request to show a tracking report when tracking report viewing not permitted."
            );
            return;
        }

        let modalInstance = mModalSvc.get(this).open({
            animation: true,
            template: EventTrackingReportDialogController.template,
            controller: EventTrackingReportDialogController,
            controllerAs: EventTrackingReportDialogController.controllerAs,
            backdropClass: modalBackdropClass,
            windowClass: modalWindowClass,
            size: modalSizeLarge,
            resolve: {
                trackingReportData: () => {
                    let trackingEventId = this.currentCalendarEvent.EventId;
                    return mTrackingEventReportDataSvc.get(this)
                        .get({eventId: trackingEventId})
                        .$promise
                        .then(
                            (eventReportData) => eventReportData.result,
                            (errorMsg) => {
                                mLogSvc.get(this).error(errorMsg);
                            }
                        );
                },
                trackingEventName: () => this.currentCalendarEvent.eventDisplayName
            }

        });

        modalInstance.result.then(
            () => {
                mLogSvc.get(this).debug("Done!");
            },
            (errorMsg) => {
                if (errorMsg === "cancel") {
                    mLogSvc.get(this).debug("Report dialog cancelled");
                }
                else {
                    mLogSvc.get(this).warn("Problem!");
                }
            }
        );
    }

    showDirectLinkDialog() {

        if (this.currentCalendarEvent == null)
        {
            return;
        }

        let directLinkUrl = mUiRouterStateSvc.get(this).href(
            EventViewStateConstants.stateName,
            {
                eventId: this.currentCalendarEvent.EventId
            },
            {
                absolute: true
            });

        let directLinkModalInstance = mModalSvc.get(this).open({
            animation: true,
            template: CalendarEventDirectLinkDialogController.template,
            controller: CalendarEventDirectLinkDialogController,
            controllerAs: CalendarEventDirectLinkDialogController.controllerAs,
            backdropClass: modalBackdropClass,
            windowClass: modalWindowClass,
            resolve: {
                directLink: () => directLinkUrl
            }
        });

        directLinkModalInstance.result.then(() => {
            mLogSvc.get(this).debug("Direct Link Panel: Done!");
        });

    }

}

CalendarEventInfoPanelController.$inject = [
    "$scope",
    "$log",
    "$q",
    "$timeout",
    "$uibModal",
    "$state",
    "TrackingCalendarData",
    "StatusClass",
    "UserInfoService",
    "TrackingEventReportData"
];

CalendarEventInfoPanelController.dependencies = DependencyListBuilder(
    EventTrackingReportDialogController.dependencies,
    CalendarEventDirectLinkDialogController.dependencies,
    [
        ngSanitize,
        ngAnimate,
        ngAria,
        uiBootstrapModule,
        kendoDirectives,
        uiRouterModule,
        UserInfoServiceModule,
        TrackingCalendarDataServiceModule,
        StatusClassServiceModule,
        TrackingEventReportDataServiceModule,
        StatusLabelComponentModule,
        CalendarEventDateTimeComponentModule,
        OfficeTypeLabelTypeFilterModule,
        CurrentCandidaciesFilterModule
    ]
);

CalendarEventInfoPanelController.template = require("./CalendarEventInfoPanel.tpl.html");

CalendarEventInfoPanelController.controllerAs = "eventInfoPanel";

export default CalendarEventInfoPanelController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-calendar-view/CalendarEventInfoPanelController.js
// module id = c2fh
// module chunks = 1